/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useDispatch } from 'react-redux';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

/**
 * A dispatch that accepts thunks as well as plain actions.
 *
 * The state parameter is `unknown` on purpose. shared-ui is a library: the store
 * it dispatches into is built by whichever service mounted the component, and
 * every service composes a different root reducer (see
 * `ui-navigation/src/redux/store.ts`). There is no single state type shared-ui
 * could name here that would be true at runtime, so it names none. That costs
 * nothing in practice — shared-ui reads state through `useSelector` with an
 * explicit slice type from `./state.types`, never from inside a thunk.
 *
 * The extra-argument parameter is `unknown` for the same reason: no service in
 * this repo configures a thunk extra argument.
 */
export type TypedDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

/**
 * Use instead of `useDispatch` in shared-ui when dispatching a thunk.
 *
 * Deliberately *not* named `useAppDispatch`. About ten services already export
 * their own `useAppDispatch` from `src/redux/redux-helpers.ts`, bound to that
 * service's real `RootState`. Those are strictly better inside their own
 * service, and shared-ui must not look like a competing answer to the same
 * question — hence the different name.
 *
 * @example
 * const dispatch = useTypedDispatch();
 * dispatch(CartActions.fetchCart(true)); // a sarsaparilla thunk
 * dispatch(closeAuthModal());            // a plain RTK action
 */
export function useTypedDispatch(): TypedDispatch {
    return useDispatch<TypedDispatch>();
}
